import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import useRequest from '../../../../../axios/apis/useRequest.js'
import {
  Modal,
  Button,
  Form,
  OverlayTrigger,
  Tooltip,
} from '@themesberg/react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import { Wrapper } from './PlanForm.styled.jsx'
import { useParams } from 'react-router-dom'
import {
  PlanInfo,
  setAllPlans,
} from '../../../../../store/slices/products/productsSlice.js'
import TextareaAndCounter from '../../../Shared/TextareaAndCounter/TextareaAndCounter.jsx'
import AutoGenerateInput from '../../../Shared/AutoGenerateInput/AutoGenerateInput.jsx'
import MultilingualInput from '../../../Shared/MultilingualInput/MultilingualInput.jsx'
import { activeTab } from '../../../../../const/product.js'
import SafeFormatMessage from '../../../Shared/SafeFormatMessage/SafeFormatMessage.jsx'
import { BsFillQuestionCircleFill } from 'react-icons/bs'
import ProWizard from '../../../../../pages/ProWizard/ProWizard.jsx'

const PlanForm = ({
  type,
  planData,
  setVisible,
  popupLabel,
  setActiveIndex,
  quickSetup,
}) => {
  const { createPlanRequest, editPlanRequest, getProductPlans } = useRequest()
  const dispatch = useDispatch()
  const routeParams = useParams()
  const productId = routeParams.id || routeParams.productId
  const allProducts = useSelector((state) => state.products.products)
  const ProductTrialType = allProducts[productId]?.trialType

  const extractRedirectionLink = (description) => {
    const match = description.match(/#redirection-link=([^#]+)#/)
    return match ? match[1] : ''
  }
  const [redirectionLink, setRedirectionLink] = useState(
    planData?.descriptionLocalizations?.en
      ? extractRedirectionLink(planData.descriptionLocalizations.en)
      : '' || planData?.descriptionLocalizations?.ar
        ? extractRedirectionLink(planData.descriptionLocalizations.ar)
        : ''
  )

  // Remove the redirection link from the description
  const cleanDescription = (description) => {
    return description.replace(/#redirection-link=([^#]+)#/, '').trim()
  }
  const initialValues = {
    displayNameEn: planData?.displayNameLocalizations?.en || '',
    displayNameAr: planData?.displayNameLocalizations?.ar || '',
    systemName: planData ? planData.systemName : '',
    displayOrder: planData ? planData.displayOrder : '0',
    alternativePlanID: planData ? planData.alternativePlanID : '',
    trialPeriodInDays: planData ? planData.trialPeriodInDays : '0',
    isAvailableForSelection: planData ? planData.isAvailableForSelection : true,
    descriptionEn: planData?.descriptionLocalizations?.en
      ? cleanDescription(planData.descriptionLocalizations.en)
      : '',
    descriptionAr: planData?.descriptionLocalizations?.ar
      ? cleanDescription(planData.descriptionLocalizations.ar)
      : '',

    redirectionLink: planData?.descriptionLocalizations?.en
      ? extractRedirectionLink(planData.descriptionLocalizations.en)
      : '',
  }

  const validationSchema = Yup.object().shape({
    systemName: Yup.string()
      .max(100, <SafeFormatMessage id="Must-be-maximum-100-digits" />)
      .required(<SafeFormatMessage id="Unique-Name-is-required" />)
      .matches(
        /^[a-zA-Z0-9_-]+$/,
        <SafeFormatMessage id="English-Characters,-Numbers,-and-Underscores-are-only-accepted." />
      ),
    displayNameEn: Yup.string().test({
      name: 'displayNameRequired',
      message: <SafeFormatMessage id="Display-Name-is-required" />,
      test: (value, context) => {
        const { parent } = context
        const displayNameEn = parent.displayNameEn
        const displayNameAr = parent.displayNameAr
        return !!displayNameEn || !!displayNameAr
      },
    }),
    displayNameAr: Yup.string().test({
      name: 'displayNameRequired',
      message: <SafeFormatMessage id="Display-Name-is-required" />,
      test: (value, context) => {
        const { parent } = context
        const displayNameEn = parent.displayNameEn
        const displayNameAr = parent.displayNameAr
        return !!displayNameEn || !!displayNameAr
      },
    }),
    descriptionEn: Yup.string().max(
      250,
      <SafeFormatMessage id="Must-be-maximum-250-digits" />
    ),
    descriptionAr: Yup.string().max(
      250,
      <SafeFormatMessage id="Must-be-maximum-250-digits" />
    ),
    displayOrder: Yup.number()
      .typeError(<SafeFormatMessage id="Display-Order-must-be-a-number" />)
      .integer(<SafeFormatMessage id="Display-Order-must-be-an-integer" />)
      .min(
        0,
        <SafeFormatMessage id="Display-Order-must-be-a-positive-number" />
      )
      .default(0),
    trialPeriodInDays: Yup.number(),
  })

  useEffect(() => {
    const fetchData = async () => {
      if (!allProducts[productId].plan) {
        try {
          const plan = await getProductPlans(productId)
          dispatch(
            setAllPlans({
              productId: productId,
              data: plan.data.data,
            })
          )
        } catch (error) {
          console.error('Error fetching product plans:', error)
        }
      }
    }

    fetchData()
  }, [])

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const descriptionWithRedirectionEn = values.isAvailableForSelection
        ? values.descriptionEn
        : values.descriptionEn
          ? redirectionLink
            ? `${values.descriptionEn} #redirection-link=${redirectionLink}#`
            : values.descriptionEn
          : ''
      const descriptionWithRedirectionAr =
        values.isAvailableForSelection && redirectionLink
          ? values.descriptionAr
          : values.descriptionAr
            ? redirectionLink
              ? `${values.descriptionAr} #redirection-link=${redirectionLink}#`
              : values.descriptionAr
            : ''

      const dataToSubmit = {
        isLockedBySystem: false,
        tenancyType: 3,
        systemName: values.systemName,
        displayNameLocalizations: {
          en: values.displayNameEn,
          ar: values.displayNameAr,
        },
        descriptionLocalizations: {
          en: descriptionWithRedirectionEn,
          ar: descriptionWithRedirectionAr,
        },
        displayOrder: values.displayOrder || 0,
        alternativePlanID: values.alternativePlanID || null,
        trialPeriodInDays: values.trialPeriodInDays || 0,
        isAvailableForSelection: values.isAvailableForSelection,
      }

      if (type === 'create') {
        const createPlan = await createPlanRequest(productId, {
          ...dataToSubmit,
          productId: productId,
        })

        dispatch(
          PlanInfo({
            planId: createPlan.data.data.id,
            productId: productId,
            data: {
              ...dataToSubmit,
              editedDate: new Date().toISOString().slice(0, 19),
              createdDate: new Date().toISOString().slice(0, 19),
              id: createPlan.data.data.id,
              isVisible: false,
            },
          })
        )

        if (setActiveIndex) {
          setActiveIndex(activeTab.plans)
        }
      } else {
        await editPlanRequest(productId, {
          data: dataToSubmit,
          id: planData.id,
        })

        dispatch(
          PlanInfo({
            planId: planData.id,
            productId: productId,
            data: {
              ...planData,
              ...dataToSubmit,
              editedDate: new Date().toISOString().slice(0, 19),
              createdDate: planData.createdDate,
              id: planData.id,
            },
          })
        )
      }

      setVisible && setVisible(false)
      setSubmitting(false)
    },
  })

  let alternativePlanIDOptions

  if (allProducts[productId]?.plans) {
    alternativePlanIDOptions = Object.values(allProducts[productId].plans)
      .filter((item) => planData?.id != item.id)
      .map((item) => ({
        value: item.id,
        label: item.displayName,
      }))
  } else {
    alternativePlanIDOptions = []
  }
  const direction = useSelector((state) => state.main.direction)

  return (
    <Wrapper>
      <Form onSubmit={formik.handleSubmit}>
        {!quickSetup && (
          <Modal.Header>
            <Modal.Title className="h6">{popupLabel}</Modal.Title>
            <Button
              variant="close"
              aria-label="Close"
              onClick={() => setVisible(false)}
            />
          </Modal.Header>
        )}
        <Modal.Body>
          {/* MultilingualInput for Display Name */}
          <MultilingualInput
            inputLabel="Display-Name"
            languages={[
              { code: 'en', name: 'English' },
              { code: 'ar', name: 'Arabic' },
            ]}
            inputIds={{
              en: 'displayNameEn',
              ar: 'displayNameAr',
            }}
            placeholder={{
              en: 'English-Name',
              ar: 'Arabic-Name',
            }}
            values={{
              en: formik.values.displayNameEn,
              ar: formik.values.displayNameAr,
            }}
            onChange={formik.handleChange}
            isRequired={true}
            inputType="input"
            errors={{
              en: formik.errors.displayNameEn,
              ar: formik.errors.displayNameAr,
            }}
            touched={{
              en: formik.touched.displayNameEn,
              ar: formik.touched.displayNameAr,
            }}
          />

          {/* System Name Field */}
          <div className="mb-3">
            {type === 'create' && (
              <AutoGenerateInput
                label={<SafeFormatMessage id="System-Name" />}
                id="systemName"
                value={formik.values.displayNameEn}
                name={formik.values.systemName}
                onChange={formik.handleChange}
                onGenerateUniqueName={(generatedUniqueName) => {
                  formik.setFieldValue('systemName', generatedUniqueName)
                }}
                onAutoGenerateClick={() => {
                  formik.setFieldValue(
                    'isAutoGenerated',
                    !formik.values.isAutoGenerated
                  )
                }}
                isAutoGenerated={formik.values.isAutoGenerated}
              />
            )}
            {formik.touched.systemName && formik.errors.systemName && (
              <Form.Control.Feedback
                type="invalid"
                style={{ display: 'block' }}
              >
                {formik.errors.systemName}
              </Form.Control.Feedback>
            )}
          </div>

          {/* Display Order Field */}
          <div>
            <Form.Group className="mb-3">
              <Form.Label>
                <SafeFormatMessage id="Display-Order" />
              </Form.Label>
              <input
                type="text"
                className="form-control"
                id="displayOrder"
                name="displayOrder"
                onChange={formik.handleChange}
                value={formik.values.displayOrder}
              />
              {formik.touched.displayOrder && formik.errors.displayOrder && (
                <Form.Control.Feedback
                  type="invalid"
                  style={{ display: 'block' }}
                >
                  {formik.errors.displayOrder}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </div>

          {/* Trial Period Field */}
          {ProductTrialType == 3 && (
            <div>
              <Form.Group className="mb-3">
                <Form.Label>
                  <SafeFormatMessage id="Trial-Period-In-Days" />
                </Form.Label>
                <input
                  type="text"
                  className="form-control"
                  id="trialPeriodInDays"
                  name="trialPeriodInDays"
                  onChange={formik.handleChange}
                  value={formik.values.trialPeriodInDays}
                />
                {formik.touched.trialPeriodInDays &&
                  formik.errors.trialPeriodInDays && (
                    <Form.Control.Feedback
                      type="invalid"
                      style={{ display: 'block' }}
                    >
                      {formik.errors.trialPeriodInDays}
                    </Form.Control.Feedback>
                  )}
              </Form.Group>
            </div>
          )}

          {/* Alternative Plan Field */}
          <div>
            <Form.Group className="mb-3">
              <Form.Label>
                <SafeFormatMessage id="Alternative-Plan" />{' '}
              </Form.Label>
              <select
                className="form-control"
                name="alternativePlanID"
                id="alternativePlanID"
                value={formik.values.alternativePlanID}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={!productId}
              >
                <option value="">
                  <SafeFormatMessage id="Select-Option" />
                </option>
                {alternativePlanIDOptions?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {formik.touched.alternativePlanID &&
                formik.errors.alternativePlanID && (
                  <Form.Control.Feedback
                    type="invalid"
                    style={{ display: 'block' }}
                  >
                    {formik.errors.alternativePlanID}
                  </Form.Control.Feedback>
                )}
            </Form.Group>
          </div>

          {/* isAvailableForSelection Checkbox */}
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label={<SafeFormatMessage id="Is-Available-For-Selection" />}
              id="isAvailableForSelection"
              name="isAvailableForSelection"
              checked={formik.values.isAvailableForSelection}
              onChange={formik.handleChange}
            />
          </Form.Group>

          {/* Redirection Link Input if not available for selection */}
          {!formik.values.isAvailableForSelection && (
            <Form.Group className="mb-3">
              <Form.Label>
                <SafeFormatMessage id="Redirection-Link" />
              </Form.Label>
              <OverlayTrigger
                trigger={['hover', 'focus']}
                overlay={
                  <Tooltip>
                    <SafeFormatMessage id="Provide-a-redirection-link-here.-This-link-will-be-used-in-the-marketplace-for-unselectable-plans-to-redirect-users,-for-example,-to-##Contact-Us##." />
                  </Tooltip>
                }
              >
                <span>
                  <BsFillQuestionCircleFill
                    style={{ color: 'var(--slate-gray)' }}
                    className={
                      direction == 'rtl' ? 'ar-questionCircle mr-2' : 'ml-2'
                    }
                  />
                </span>
              </OverlayTrigger>
              <input
                type="text"
                className="form-control"
                id="redirectionLink"
                name="redirectionLink"
                placeholder="https://example.com/contact-us"
                value={redirectionLink}
                onChange={(e) => setRedirectionLink(e.target.value)}
              />
            </Form.Group>
          )}
          {/* MultilingualInput for Description with Tooltip */}

          <MultilingualInput
            inputLabel="Description"
            languages={[
              { code: 'en', name: 'English' },
              { code: 'ar', name: 'Arabic' },
            ]}
            tooltipMessageId={
              !formik.values.isAvailableForSelection &&
              'The-word-enclosed-between-##-will-be-related-to-the-redirection-link.-for-example,-to-##Contact-Us##.'
            }
            inputIds={{
              en: 'descriptionEn',
              ar: 'descriptionAr',
            }}
            placeholder={{
              en: 'English-Description',
              ar: 'Arabic-Description',
            }}
            values={{
              en: formik.values.descriptionEn,
              ar: formik.values.descriptionAr,
            }}
            onChange={formik.handleChange}
            isRequired={false}
            inputType="TextareaAndCounter"
            maxLength={250}
            errors={{
              en: formik.errors.descriptionEn,
              ar: formik.errors.descriptionAr,
            }}
            touched={{
              en: formik.touched.descriptionEn,
              ar: formik.touched.descriptionAr,
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" type="submit">
            <SafeFormatMessage id="Submit" />
          </Button>
          <Button
            variant="link"
            className="text-gray "
            onClick={() => setVisible(false)}
          >
            <SafeFormatMessage id="Close" />
          </Button>
        </Modal.Footer>
      </Form>
    </Wrapper>
  )
}

export default PlanForm
